<template>
  <modal v-bind="$attrs" v-on="$listeners">
    <div slot="header"><h6>{{$t('contract.addContract')}} {{client.name}}</h6></div>
    <form @submit.prevent="addContracts">
      <div class="row">
        <div class="col-12">
          <fg-input :error="getError($t('client.management.contract.contract'))"
                    :label="$t('client.management.contract.contract')"
                    required
          >
            <el-select class="select-default"
                       :name="$t('client.management.contract.contract')"
                       :placeholder="$t('client.management.contract.contractPlaceholder')"
                       :error="getError($t('client.management.contract.contract'))"
                       v-validate="modelValidations.required"
                       v-model="model.product_id"
                       @change="onProductChange"
                       filterable
                       clearable>
              <el-option class="select-default"
                         v-for="option in contractProducts"
                         :value="option.id"
                         :label="option.name || `${option.name} (${option.id})`"
                         :key="option.id">
              </el-option>
              <el-option class="select-default" value="new-product">
                <div class="d-flex align-items-center"><PlusIcon class="mr-1"/>{{$t('client.management.contract.newProduct')}}</div>
              </el-option>
            </el-select>
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :error="getError($t('client.management.contract.contractName'))"
                    :label="$t('client.management.contract.contractName')"
                    :placeholder="$t('client.management.contract.contractName')"
                    :name="$t('client.management.contract.contractName')"
                    v-model="model.name"
                    v-validate="modelValidations.required"
                    required
          >
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :label="$t('generic.user')"
                    :error="getError($t('generic.user'))"
                    required>
            <user-autocomplete v-model="user"
                               v-validate="modelValidations.required"
                               :search-params="{'match[client_uuid]': client.uuid}"
                               :multiple="false"
                               :name="$t('generic.user')"
                               class="w-full"
            />
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :label="$t('client.management.contract.jobBoard')"
                    :error="getError($t('client.management.contract.jobBoard'))"
                    required
          >
            <JobBoardsForProductAutocomplete v-model="model.product_job_board_id"
                                             v-validate="modelValidations.required"
                                             :name="$t('client.management.contract.jobBoard')"
                                             :disabled="selectedProduct.product_id === ''"
                                             :product-id="selectedProduct.product_id"
                                             option-key="id"
            />
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :placeholder="$t('client.management.contract.validTill')"
                    :name="$t('client.management.contract.validTill')"
                    :label="$t('client.management.contract.validTill')"
                    :picker-options="pickerOptions"
                    v-model="model.valid_till"
                    :default-value="model.valid_till"
                    :value-format="DEFAULT_DATETIME_FULL_FORMAT"
                    component="el-date-picker">
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :placeholder="$t('client.management.contract.amount')"
                    :disabled="selectedProduct.amount"
                    :name="$t('client.management.contract.amount')"
                    :error="getError($t('client.management.contract.amount'))"
                    :label="$t('client.management.contract.amount')"
                    v-validate="modelValidations.amount"
                    type="number"
                    v-model.number="model.amount">
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :placeholder="$t('client.management.contract.price')"
                    :disabled="selectedProduct.amount"
                    :name="$t('client.management.contract.price')"
                    :error="getError($t('client.management.contract.price'))"
                    :label="`${$t('client.management.contract.price')} (${currencySymbol})`"
                    v-validate="modelValidations.price"
                    type="number"
                    v-model="model.price">
          </fg-input>
        </div>
        <div class="col-12">
          <fg-input :placeholder="$t('client.management.contract.poNumber')"
                    :label="$t('client.management.contract.poNumber')"
                    :name="$t('client.management.contract.poNumber')"
                    v-model="model.po_number">
          </fg-input>
        </div>
        <div class="col-6">
          <submit-button native-type="submit"/>
        </div>
      </div>
    </form>
  </modal>
</template>
<script>
  import addYears from 'date-fns/addYears'
  import get from 'lodash/get'
  import { PlusIcon } from 'vue-feather-icons'
  import { mapGetters, mapState } from 'vuex'
  import Vue from 'vue'
  import JobBoardsForProductAutocomplete from 'src/components/Autocomplete/JobBoardsForProductAutocomplete'
  import { Select, Option, DatePicker } from 'element-ui'
  import { DEFAULT_DATETIME_FULL_FORMAT } from 'src/config/dateUtils'
  import UserAutocomplete from '@/components/Autocomplete/UserAutocomplete'
  Vue.use(DatePicker)
  export default {
    inheritAttrs: false,
    components: {
      PlusIcon,
      UserAutocomplete,
      JobBoardsForProductAutocomplete,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    props: {
      client: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        DEFAULT_DATETIME_FULL_FORMAT,
        selectedProduct: {},
        user: null,
        model: {
          name: '',
          po_number: '',
          product_id: '',
          user_uuid: '',
          client_id: this.client.uuid || this.$route.params.id,
          product_job_board_id: null,
          valid_till: this.$formatDate(addYears(new Date(), 1), DEFAULT_DATETIME_FULL_FORMAT),
          price: 0,
          amount: 0
        },
        selectedJobBoard: null,
        pickerOptions: {
          disabledDate (time) {
            return time.getTime() < Date.now();
          }
        }
      }
    },
    computed: {
      ...mapGetters('config', ['contractProducts']),
      ...mapState({
        currencySymbol: state => state.config.currencySymbol
      }),
      modelValidations () {
        return {
          amount: {
            required: true,
            min_value: 0,
            numeric: true
          },
          required: {
            required: true
          },
          price: {
            required: true,
            min_value: 0,
            numeric: true
          }
        }
      }
    },
    methods: {
      onProductChange (productId) {
        if (productId === 'new-product') {
          this.$router.push('/settings/products/create?product_type=contract')
          return
        }
        let product = this.contractProducts.find(p => p.id === productId)
        if (product) {
          this.selectedProduct = product
          this.model.amount = product.amount || 1
          this.model.price = product.price
          this.model.name = product.name
        }
      },
      async addContracts () {
        let res = await this.$validator.validate()
        if (!res) return
        this.model.user_uuid = get(this.user, 'uuid', '')
        if (typeof this.model.valid_till === 'object') {
          this.model.valid_till = this.$formatDate(this.model.valid_till, DEFAULT_DATETIME_FULL_FORMAT)
        }
        this.$emit('submit', this.model)
      }
    },
    watch: {
      'client.uuid'(newId) {
        this.model.client_id = newId
      }
    }
  }
</script>
<style>
</style>
